import Skeleton from "@/v2-ui/skeleton"
import cn from "@/v2-ui/utils/utils.cn"

export type StatusSummaryRowSkeletonProps = {
  // When true the row uses the heavier "header" treatment that
  // the live platform card uses for its top row -- darker bg,
  // bottom border, slightly more horizontal padding. Defaults to
  // false so the inner per-category rows render as plain divider
  // rows.
  asHeader?: boolean
  // Tailwind width class for the title placeholder. Lets the
  // caller stagger row widths so the skeleton looks like a real
  // list of rows with different name lengths instead of a
  // monotonous column of identical bars.
  titleWidth?: string
  // Toggle the trailing 30d-uptime placeholder. Off by default
  // because the live row hides the column when no uptime has
  // been computed yet; matching that keeps the skeleton honest.
  showUptime?: boolean
}

// One placeholder row for either the platform header or one of
// its category rows. Uses the same 12-col grid + spacing as the
// real `StatusSummaryRow` so when data lands the column edges
// don't move. Pulses are limited to the dot, the title block,
// and (optionally) the uptime number; the description / summary
// lines stay empty rather than animating every line at once. The
// 30-day strip used to live in the middle of this row -- it now
// renders inside each expanded service row, so the skeleton just
// reserves the title + uptime columns the live row uses.
function StatusSummaryRowSkeleton(
  props: Readonly<StatusSummaryRowSkeletonProps>,
) {
  const { asHeader = false, titleWidth = "w-40", showUptime = false } = props
  return (
    <div
      aria-hidden
      className={cn(
        "grid grid-cols-12 items-center gap-x-4 gap-y-2 border-b border-border/60 py-3 last:border-b-0 sm:gap-x-6",
        asHeader && "bg-muted/30 px-4 py-3 sm:px-5",
      )}
    >
      <div className="col-span-12 flex items-center gap-3 sm:col-span-10">
        <Skeleton className="size-2.5 rounded-full" />
        <div className="flex flex-1 flex-col gap-1.5">
          <Skeleton className={cn("h-4", titleWidth)} />
          <Skeleton className="h-3 w-3/4 max-w-[18rem]" />
        </div>
      </div>
      <div className="col-span-12 sm:col-span-2">
        {showUptime && <Skeleton className="ml-auto h-4 w-16" />}
      </div>
    </div>
  )
}

export default StatusSummaryRowSkeleton
